import { useState, useEffect, useCallback } from 'react';

export type TourStepId = 
  | 'welcome'
  | 'demo'
  | 'speed-explanation'
  | 'speed-ramp'
  | 'progress-tracking'
  | 'challenge'
  | 'summary'
  | 'conversion';

export interface TourStep {
  id: TourStepId;
  title: string;
  content: string;
  duration: number;
  action?: () => void;
}

export interface TourState {
  currentStep: TourStepId;
  isActive: boolean;
  isComplete: boolean;
  isSkipped: boolean;
  progress: number;
}

const TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    title: '60-Second Speed Reading Challenge',
    content: 'Ready to read 3x faster in 60 seconds?',
    duration: 5000
  },
  {
    id: 'demo',
    title: 'Watch This',
    content: 'Watch this sentence at 200 WPM',
    duration: 5000
  },
  {
    id: 'speed-explanation',
    title: 'Speed Reading Basics',
    content: "Did you catch that? Most people read 200 WPM. You're about to hit 600 WPM - that's 3x faster!",
    duration: 6000
  },
  {
    id: 'speed-ramp',
    title: 'Watch Your Brain Adapt',
    content: 'Notice how your brain adjusts?',
    duration: 15000
  },
  {
    id: 'progress-tracking',
    title: 'Progress Tracking',
    content: 'Notice how your brain adjusts - Track your improvement',
    duration: 5000
  },
  {
    id: 'challenge',
    title: 'The Challenge',
    content: 'Read this at 600 WPM',
    duration: 8000
  },
  {
    id: 'summary',
    title: 'Your Results',
    content: 'See how you did!',
    duration: 5000
  },
  {
    id: 'conversion',
    title: 'You\'re a Speed Reader Now!',
    content: 'Join 10,000+ readers who\'ve doubled their speed',
    duration: 5000
  }
];

const STORAGE_KEY = 'flashread_tour_state';

export const useOnboardingTour = () => {
  const [state, setState] = useState<TourState>({
    currentStep: 'welcome',
    isActive: false,
    isComplete: false,
    isSkipped: false,
    progress: 0
  });

  // Load saved state from localStorage (only for progress persistence, not auto-start)
  useEffect(() => {
    const savedState = localStorage.getItem(STORAGE_KEY);
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        // Load completion state but don't auto-start
        if (parsed.isComplete || parsed.isSkipped) {
          return;
        }
      } catch (e) {
        console.error('Failed to parse tour state', e);
      }
    }
  }, []);

  // Save state to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  // Calculate progress based on current step
  useEffect(() => {
    const stepIndex = TOUR_STEPS.findIndex(s => s.id === state.currentStep);
    const progress = ((stepIndex + 1) / TOUR_STEPS.length) * 100;
    setState(prev => ({ ...prev, progress }));
  }, [state.currentStep]);

  const startTour = useCallback(() => {
    // Reset state in localStorage
    localStorage.removeItem(STORAGE_KEY);
    setState({
      currentStep: 'welcome',
      isActive: true,
      isComplete: false,
      isSkipped: false,
      progress: 0
    });
  }, []);

  const nextStep = useCallback(() => {
    const currentIndex = TOUR_STEPS.findIndex(s => s.id === state.currentStep);
    const nextIndex = currentIndex + 1;

    if (nextIndex >= TOUR_STEPS.length) {
      setState(prev => ({
        ...prev,
        isActive: false,
        isComplete: true,
        progress: 100
      }));
    } else {
      const nextStep = TOUR_STEPS[nextIndex];
      setState(prev => ({ ...prev, currentStep: nextStep.id }));
      
      // Execute action if defined
      if (nextStep.action) {
        nextStep.action();
      }
    }
  }, [state.currentStep]);

  const prevStep = useCallback(() => {
    const currentIndex = TOUR_STEPS.findIndex(s => s.id === state.currentStep);
    if (currentIndex > 0) {
      const prevStep = TOUR_STEPS[currentIndex - 1];
      setState(prev => ({ ...prev, currentStep: prevStep.id }));
    }
  }, [state.currentStep]);

  const skipTour = useCallback(() => {
    setState(prev => ({
      ...prev,
      isActive: false,
      isSkipped: true
    }));
  }, []);

  const resetTour = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setState({
      currentStep: 'welcome',
      isActive: false,
      isComplete: false,
      isSkipped: false,
      progress: 0
    });
  }, []);

  const getCurrentStep = useCallback((): TourStep | undefined => {
    return TOUR_STEPS.find(s => s.id === state.currentStep);
  }, [state.currentStep]);

  const getStepIndex = useCallback((): number => {
    return TOUR_STEPS.findIndex(s => s.id === state.currentStep);
  }, [state.currentStep]);

  return {
    state,
    steps: TOUR_STEPS,
    startTour,
    nextStep,
    prevStep,
    skipTour,
    resetTour,
    getCurrentStep,
    getStepIndex,
    totalSteps: TOUR_STEPS.length
  };
};

export default useOnboardingTour;
